export const formatUrl = (path: string) => {
  if (!path) return "";
  const normalized = path.replace(/^\/+/, "");
  return normalized.includes("API/")
    ? `/api/${normalized.split("API/")[1]}`
    : normalized.includes("api/") ? normalized : `api/${normalized}`;
};

export const fileExtensions = {
  image: new Set(["jpg", "jpeg", "png"]),
  pdf: new Set(["pdf"]),
  word: new Set(["doc", "docx", "txt"]),
  excel: new Set(["xls", "xlsx"]),
  video: new Set(["mp4", "mov", "avi", "mkv", "webm"]),
};

const getParsedFileInfo = (path: string) => {
  if (!path || typeof path !== "string") return { name: "", extension: "" };

  const cleanPath = path.split("?")[0];
  const fullName = cleanPath ? cleanPath.substring(cleanPath.lastIndexOf("/") + 1) : "file";
  const cleanName = fullName.replace(/^[a-zA-Z0-9-]+_/, "");
  const extension = cleanName.split(".").pop()?.toLowerCase() || "";

  return { name: cleanName, extension };
};

export const extractFileInfo = (path: string) => getParsedFileInfo(path);

export const checkFileType = (fileUrl: string, fileName: string, extension: string) => {
  if (!fileUrl) return;

  const ext = extension?.toLowerCase();

  if (
    fileExtensions.pdf.has(ext) ||
    fileExtensions.video.has(ext) ||
    fileExtensions.image.has(ext)
  ) {
    window.open(fileUrl, "_blank");
  } else {
    const a = document.createElement("a");
    a.href = fileUrl;
    a.target = "_blank";
    a.download = fileName || "downloadFile";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
};

const getFileLabel = (extension: string) => {
  const ext = extension?.toLowerCase();

  if (fileExtensions.image.has(ext)) return "Image";
  if (fileExtensions.video.has(ext)) return "Video";
  if (fileExtensions.pdf.has(ext)) return "PDF";
  if (fileExtensions.word.has(ext)) return "Document";

  return "File";
};

export const pushFile = (
  path: string,
  list: AttachmentItem[],
  type?: string
) => {
  if (!path) return;

  const { name, extension } = type === "blob" ? extractAssetFileInfo(path) : extractFileInfo(path);

  const ext = extension?.toLowerCase();

  let label = "File";

  if (fileExtensions.image.has(ext)) {
    label = "Image";
  } else if (fileExtensions.video?.has(ext)) {
    label = "Video";
  } else if (fileExtensions.pdf.has(ext)) {
    label = "PDF";
  } else if (fileExtensions.word.has(ext)) {
    label = "Document";
  }

  list.push({
    label,
    name,
    extension,
    url: type === "blob" ? path : formatUrl(path),
  });
};

export const extractAssetFileInfo = (path: string) => getParsedFileInfo(path);

export interface AttachmentItem {
  label: string;
  name: string;
  extension: string;
  url: string;
};

export const isDateDifferent = (d1?: string, d2?: string) => {
  if (!d1 || !d2) return false;

  const t1 = Math.floor(new Date(d1).getTime() / 60000);
  const t2 = Math.floor(new Date(d2).getTime() / 60000);

  return t1 !== t2;
};

export const colorMap: Record<string, string> = {
  red: "#ff0000",
  yellow: "#ffd700",
  green: "#008000",
  pink: "#ff1493",
};

export const getColor = (color?: string) => colorMap[color?.toLowerCase() || ""] || "transparent";

export const getSaleOrderStatusLabel = (status: string) => {
  if (status === "Accept") return "Approved";
  else if (status === "PartialDelivered") return "Partial Delivered";
  else if (status === "Reject") return "Rejected";
  else return status || "";
};

export const assetStatusList = [
  { label: "Active", value: true },
  { label: "Inactive", value: false }
];

export const itemStatusList = [
  { label: "Available", value: "Available" },
  { label: "InRepair", value: "InRepair" },
  { label: "Lost", value: "Lost" },
  { label: "Scrapped", value: "Scrapped" },
];

const statusColors: Record<string, string> = {
  "Available": "#66BB6A",
  "Assigned": "#3949AB",
  "InRepair": "#FFA726",
  "Lost": "#EF5350",
  "Scrapped": "#D32F2F"
};

export const getItemStatusColor = (status: string) => {
  return statusColors[status] || "#BDBDBD";
};

export const isRemainingHourZero = (hours?: string) => {
  if (!hours) return 0;

  const parts = hours.split(":");

  if (parts.length > 0) {
    return parseInt(parts.join("").replace(",", ""));
  }

  return 0;
};

const ATTENDANCE_MAP: Record<
  string,
  { short: string; color: string }
> = {
  present: { short: "P", color: "#22C55E" },       // neon green
  absent: { short: "A", color: "#F43F5E" },        // neon pink/red
  holiday: { short: "H", color: "#F59E0B" },       // amber
  halfday: { short: "HD", color: "#EAB308" },      // yellow
  extraday: { short: "E", color: "#8B5CF6" },     // purple
  plannedleave: { short: "PL", color: "#06B6D4" }, // cyan
  earnedleave: { short: "ELR", color: "#0EA5E9" }, // sky blue
  emergencyleave: { short: "EML", color: "#14B8A6" }, // teal
  casualleave: { short: "CL", color: "#A855F7" },  // violet
  maternityleave: { short: "ML", color: "#EC4899" }, // pink
  sickleave: { short: "SL", color: "#3B82F6" },    // blue
  paidleave: { short: "PD", color: "#84CC16" },    // lime
  festivalleave: { short: "FL", color: "#F97316" }, // orange
};

export const getAttendanceStatus = (status: string = "") => {
  if (!status || typeof status !== "string") return "-";

  const key = status.trim().toLowerCase().replace(/\s+/g, "");

  const mapped = ATTENDANCE_MAP[key];

  if (mapped?.short) return mapped.short;

  const words = status.trim().split(/\s+/).filter(Boolean);

  if (words.length === 0) return "-";

  if (words.length === 1) {
    return (words[0]?.[0] || "-").toUpperCase();
  }

  return words.map((w) => (w?.[0] || "").toUpperCase()).join("") || "-";
};

const DEFAULT_COLORS = [
  "#A2CD77",
  "#FC4E4E",
  "#67D8FF",
  "#FFA100",
  "#B39DDB",
  "#80CBC4",
  "#FFAB91",
  "#90CAF9"
];

const hashString = (str: string) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return hash;
};

export const getAttendanceStatusColor = (status: string = "") => {
  if (!status) return "#E0E0E0";

  const key = status.trim().toLowerCase().replace(/\s+/g, "");

  const mapped = ATTENDANCE_MAP[key];

  if (mapped?.color) return mapped.color;

  const index = hashString(key) % DEFAULT_COLORS.length;
  return DEFAULT_COLORS[index];
};

export const months = [
  { value: 1, label: "Jan" },
  { value: 2, label: "Feb" },
  { value: 3, label: "Mar" },
  { value: 4, label: "Apr" },
  { value: 5, label: "May" },
  { value: 6, label: "Jun" },
  { value: 7, label: "Jul" },
  { value: 8, label: "Aug" },
  { value: 9, label: "Sep" },
  { value: 10, label: "Oct" },
  { value: 11, label: "Nov" },
  { value: 12, label: "Dec" },
];

export const getStatusColor = (status: string) => {
  switch (status) {
    case "Approved":
    case "Accept":
      return "#4caf50";

    case "Rejected":
    case "Reject":
      return "#f44336";

    case "Pending":
      return "#ff9800";

    case "Delivered":
      return "#3949AB";

    case "PartialDelivered":
      return "#5C6BC0";

    case "Expired":
      return "#1d1d1d";

    default:
      return "#9E9E9E";
  }
};

export const userStatusList = [
  { label: "Active Users", value: true },
  { label: "Inactive Users", value: false },
];

export const punchStatusList = [
  { value: "punched_in", label: "Punched In" },
  { value: "punched_out", label: "Punched Out" },
  { value: "not_punched_in", label: "Not Punched In" },
  { value: "leave", label: "On Leave" },
];

export const trackingStatusList: any[] = [
  { label: "Tracking", value: true },
  { label: "Not Tracking", value: false },
];

export const getInitials = (name = "") => {
  return name
    .trim()
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((word: string) => word.charAt(0).toUpperCase())
    .join("");
};

export const getAvatarColor = (name = "") => {
  const colors = [
    "#E8EDFF",
    "#DDF8EF",
    "#FFF0DD",
    "#FFE5ED",
    "#E8E2FF",
    "#DDF3FF",
  ];

  const index = name.split("")
    .reduce((acc: number, char: string) =>
      acc + char.charCodeAt(0),
      0
    ) % colors.length;

  return colors[index];
};

export const getAvatarTextColor = (name = "") => {
  const colors = [
    "#3159C9",
    "#20A86B",
    "#D98520",
    "#D63E70",
    "#6751C8",
    "#198CB5",
  ];

  const index = name.split("")
    .reduce(
      (acc: number, char: string) =>
        acc + char.charCodeAt(0),
      0
    ) % colors.length;

  return colors[index];
};

const formatTime = (dateTime?: string) => {
  if (!dateTime) return "";

  const date = new Date(dateTime);

  if (Number.isNaN(date.getTime())) {
    return dateTime?.substring(11, 16) || "";
  }

  return date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export const getMemberStatus = (member: any) => {
  if (member.IsOnLeave) {
    return {
      type: "leave",
      label: "On Leave",
    };
  }

  if (member.CheckOutTime) {
    return {
      type: "out",
      label: `Punched out • ${formatTime(
        member.CheckOutTime
      )}`,
    };
  }

  if (member.CheckInTime) {
    return {
      type: "in",
      label: `Punched in • ${formatTime(
        member.CheckInTime
      )}`,
    };
  }

  return {
    type: "waiting",
    label: "Waiting for punch in",
  };
};